import styled, { css } from "styled-components";

const colors = {
    primary: '#7367F0',
    primaryDark: '#5A54D1',
    primaryLight: '#F0EEFF',
    textDark: '#5E5873',
    textMedium: '#6E6B7B',
    textLight: '#B9B9C3',
    border: '#EBE9F1',
    borderFocus: '#7367F0',
    background: '#F8F8F8',
    white: '#FFFFFF',
    success: '#28C76F',
    successLight: '#E5F8ED',
    danger: '#EA5455',
    dangerLight: '#FCEAEA',
};

const Cont = styled.div`
    max-width: 720px;
    padding: 24px 16px;
    margin: 0 auto;
`;

const SubCont = styled.div`
    background: ${colors.white};
    padding: 28px 24px;
    border-radius: 10px;
    border: 1px solid ${colors.border};
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.08);
`;

const Title = styled.h1`
    color: ${colors.textDark};
    font-size: 1.35rem;
    font-weight: 600;
    margin: 0 0 20px 0;
`;

const SubTitle = styled.h2`
    color: ${colors.textDark};
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 16px 0;
    padding-bottom: 10px;
    border-bottom: 1px solid ${colors.border};
`;

const ContForm = styled.form`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
`;

const ContForm2 = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    ${props => props.fullWidth && css`grid-column: 1 / -1;`}
`;

const Label = styled.label`
    color: ${colors.textMedium};
    font-size: 0.85rem;
    font-weight: 500;
    letter-spacing: 0.01em;
`;

const Input = styled.input`
    display: block;
    width: 100%;
    height: 38px;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.45;
    color: ${colors.textMedium};
    padding: 0.438rem 1rem;
    background: ${colors.white};
    background-clip: padding-box;
    border: 1px solid ${colors.border};
    border-radius: 6px;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:focus {
        border-color: ${colors.borderFocus};
        box-shadow: 0 0 5px rgba(115, 103, 240, 0.35);
        outline: none;
    }

    &:read-only {
        background: ${colors.background};
        cursor: default;
    }

    &::placeholder {
        color: ${colors.textLight};
        opacity: 1;
    }
`;

const Button = styled.button`
    padding: 8px 20px;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    outline: none;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: ${colors.primary};
    color: ${colors.white};

    &:hover:not(:disabled) {
        background: ${colors.primaryDark};
        transform: translateY(-1px);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`;

const OutlineButton = styled(Button)`
    background: transparent;
    color: ${colors.primary};
    border: 1px solid ${colors.primary};

    &:hover:not(:disabled) {
        background: ${colors.primaryLight};
        transform: translateY(-1px);
    }
`;

const LinkButton = styled.button`
    background: none;
    border: none;
    color: ${colors.primary};
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    padding: 4px 0;
    transition: color 0.15s;

    &:hover {
        color: ${colors.primaryDark};
        text-decoration: underline;
    }
`;

const ContFormButton = styled.div`
    grid-column: 1 / -1;
`;

const BtnShowPass = styled.button`
    position: absolute;
    top: 5px;
    right: 10px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    color: ${colors.textLight};
    background: transparent;
    cursor: pointer;
    transition: 0.2s;
    outline: none;

    &:hover {
        background: ${colors.background};
        color: ${colors.textMedium};
    }
`;

const StepBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
`;

const StepItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const StepNumber = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
    flex-shrink: 0;
    transition: all 0.25s ease;

    ${props => props.$active && css`
        background: ${colors.primary};
        color: ${colors.white};
    `}
    ${props => props.$completed && css`
        background: ${colors.success};
        color: ${colors.white};
    `}
    ${props => !props.$active && !props.$completed && css`
        background: ${colors.border};
        color: ${colors.textLight};
    `}
`;

const StepLabel = styled.span`
    font-size: 0.8rem;
    font-weight: 500;
    white-space: nowrap;

    ${props => props.$active && css`color: ${colors.primary};`}
    ${props => props.$completed && css`color: ${colors.success};`}
    ${props => !props.$active && !props.$completed && css`color: ${colors.textLight};`}
`;

const StepConnector = styled.div`
    width: 40px;
    height: 2px;
    margin: 0 8px;
    transition: background 0.25s ease;
    background: ${props => props.$completed ? colors.success : colors.border};
`;

const FilterToggleGroup = styled.div`
    display: inline-flex;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid ${colors.border};
`;

const FilterToggleBtn = styled.button`
    padding: 6px 14px;
    font-size: 0.8rem;
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    outline: none;

    ${props => props.$active
        ? css`
            background: ${colors.primary};
            color: ${colors.white};
        `
        : css`
            background: ${colors.white};
            color: ${colors.textMedium};
            &:hover { background: ${colors.primaryLight}; }
        `
    }
`;

const TagContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
`;

const Tag = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    background: ${colors.primaryLight};
    color: ${colors.primary};
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 500;

    & > button {
        background: none;
        border: none;
        color: ${colors.primary};
        cursor: pointer;
        font-size: 1rem;
        line-height: 1;
        padding: 0;
        opacity: 0.7;
        transition: opacity 0.15s;
        &:hover { opacity: 1; }
    }
`;

const CheckboxLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 0.85rem;
    color: ${colors.textMedium};

    & > input[type="checkbox"] {
        accent-color: ${colors.primary};
        width: 16px;
        height: 16px;
        cursor: pointer;
    }
`;

const StatusText = styled.p`
    font-size: 0.8rem;
    margin: 0;
    padding: 8px 12px;
    border-radius: 6px;

    ${props => props.$variant === 'info' && css`
        color: ${colors.primary};
        background: ${colors.primaryLight};
    `}
    ${props => props.$variant === 'error' && css`
        color: ${colors.danger};
        background: ${colors.dangerLight};
    `}
    ${props => props.$variant === 'success' && css`
        color: ${colors.success};
        background: ${colors.successLight};
    `}
    ${props => props.$variant === 'muted' && css`
        color: ${colors.textLight};
        background: transparent;
        padding: 0;
    `}
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid ${colors.border};
    margin: 4px 0;
`;

const ContEnterprisesSelect = styled.div`
    margin-bottom: 15px;
`;

const TitleReport = styled.h1`
    grid-column: 1 / -1;
    width: 100%;
`;

const customStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '38px',
        borderColor: state.isFocused ? colors.borderFocus : colors.border,
        boxShadow: state.isFocused ? '0 0 5px rgba(115, 103, 240, 0.35)' : 'none',
        borderRadius: '6px',
        fontSize: '0.875rem',
        '&:hover': {
            borderColor: state.isFocused ? colors.borderFocus : '#D0CFD6',
        },
    }),
    menu: (base) => ({
        ...base,
        borderRadius: '6px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        border: `1px solid ${colors.border}`,
        zIndex: 10,
    }),
    menuList: (base) => ({
        ...base,
        maxHeight: '200px',
        padding: '4px',
    }),
    option: (base, state) => ({
        ...base,
        borderRadius: '4px',
        fontSize: '0.85rem',
        color: state.isSelected ? colors.white : colors.textDark,
        backgroundColor: state.isSelected ? colors.primary : state.isFocused ? colors.primaryLight : colors.white,
        cursor: 'pointer',
        '&:active': {
            backgroundColor: state.isSelected ? colors.primary : '#E8E6FF',
        },
    }),
    placeholder: (base) => ({
        ...base,
        color: colors.textLight,
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: colors.primaryLight,
        borderRadius: '4px',
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: colors.primary,
        fontWeight: 500,
        fontSize: '0.8rem',
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: colors.primary,
        borderRadius: '0 4px 4px 0',
        '&:hover': {
            backgroundColor: colors.primary,
            color: colors.white,
        },
    }),
};

const RC_Style = {
    Cont,
    SubCont,
    ContForm,
    ContForm2,
    FormGroup,
    Label,
    Input,
    Title,
    SubTitle,
    ContFormButton,
    TitleReport,
    ContEnterprisesSelect,
    Button,
    OutlineButton,
    LinkButton,
    StepBar,
    StepItem,
    StepNumber,
    StepLabel,
    StepConnector,
    FilterToggleGroup,
    FilterToggleBtn,
    TagContainer,
    Tag,
    CheckboxLabel,
    StatusText,
    Divider,
    BtnShowPass,
    customStyles,
};

export default RC_Style;
